import { useCallback, useRef, type TouchEvent } from "react";

export interface UsePinchZoomOptions {
  /**
   * 拡大方向 (= 2 指間距離が threshold 倍以上に広がる) で呼ぶ。
   * fontSize を 1 段階上げる用途を想定。
   */
  onZoomIn?: () => void;
  /**
   * 縮小方向 (= 2 指間距離が 1/threshold 倍以下に狭まる) で呼ぶ。
   * fontSize を 1 段階下げる用途を想定。
   */
  onZoomOut?: () => void;
  /**
   * scale 判定閾値 (= default 1.2)。 currentDistance / initialDistance >= threshold
   * で onZoomIn、 1/threshold 以下で onZoomOut 発火。 1 touchmove で複数回発火しない (=
   * fired ref で gating、 touchend / 3 指接触で reset)。
   */
  threshold?: number;
}

export interface UsePinchZoomHandlers {
  onTouchStart: (event: TouchEvent<HTMLElement>) => void;
  onTouchMove: (event: TouchEvent<HTMLElement>) => void;
  onTouchEnd: (event: TouchEvent<HTMLElement>) => void;
}

function distance(
  t1: { clientX: number; clientY: number },
  t2: { clientX: number; clientY: number },
): number {
  const dx = t1.clientX - t2.clientX;
  const dy = t1.clientY - t2.clientY;
  return Math.sqrt(dx * dx + dy * dy);
}

/**
 * 2 指 pinch gesture を zoom-in / zoom-out callback + threshold で受ける hook。
 * touchstart で 2 指接触時に initial 距離を記録、 touchmove で current 距離と
 * 比率を計算、 threshold 越えで該当 callback を 1 回だけ発火する (= fired ref
 * で 1 gesture 1 発火に gating)。 touchend / 1 指離脱 / 3 指接触で gesture 終了
 * + reset。
 *
 * 使用例:
 *   const handlers = usePinchZoom({
 *     onZoomIn: () => upgradeFontSize(),
 *     onZoomOut: () => downgradeFontSize(),
 *     threshold: 1.3,
 *   });
 *   return <div {...handlers}>scene 本文</div>;
 *
 * useSwipeGesture / useLongPress と併用可能。 1 指 swipe / 長押しは pinch 系
 * touch event で initial 記録のみで干渉せず、 親側で combinedTouch* で chain
 * させる pattern (= SceneView の Phase 4.1/4.3 wiring と同様)。
 *
 * mobile-pwa Phase 4.2 で SceneView の reader-style-preset.fontSize 3 段階
 * (= small / medium / large) 切替に配線想定。
 */
export function usePinchZoom(
  options: UsePinchZoomOptions,
): UsePinchZoomHandlers {
  const { onZoomIn, onZoomOut, threshold = 1.2 } = options;
  const initialDistanceRef = useRef<number | null>(null);
  const firedRef = useRef<boolean>(false);

  const onTouchStart = useCallback((event: TouchEvent<HTMLElement>) => {
    // 2 指接触のみ pinch 候補。 それ以外 (= 1 / 3+ 指) は reset。
    if (event.touches.length !== 2) {
      initialDistanceRef.current = null;
      firedRef.current = false;
      return;
    }
    const t1 = event.touches[0];
    const t2 = event.touches[1];
    if (t1 === undefined || t2 === undefined) return;
    initialDistanceRef.current = distance(t1, t2);
    firedRef.current = false;
  }, []);

  const onTouchMove = useCallback(
    (event: TouchEvent<HTMLElement>) => {
      const initial = initialDistanceRef.current;
      if (initial === null || initial === 0) return;
      if (firedRef.current) return;
      if (event.touches.length !== 2) return;
      const t1 = event.touches[0];
      const t2 = event.touches[1];
      if (t1 === undefined || t2 === undefined) return;
      const current = distance(t1, t2);
      const ratio = current / initial;
      if (ratio >= threshold) {
        firedRef.current = true;
        onZoomIn?.();
      } else if (ratio <= 1 / threshold) {
        firedRef.current = true;
        onZoomOut?.();
      }
    },
    [onZoomIn, onZoomOut, threshold],
  );

  const onTouchEnd = useCallback((_event: TouchEvent<HTMLElement>) => {
    initialDistanceRef.current = null;
    firedRef.current = false;
  }, []);

  return { onTouchStart, onTouchMove, onTouchEnd };
}
